/* =========================
   PEGUU — ARMA TUS STICKERS
   PASO 2: MEDIDA Y CANTIDAD
========================= */

(() => {
  "use strict";

  /* =========================
     CONFIGURACIÓN
  ========================== */

  const MATERIAL_STORAGE_KEY =
    "peguu:selected-material";

  const DEFAULT_QUANTITIES = [
    25,
    50,
    100,
    250,
    500,
    1000,
    2000
  ];

  const CUSTOM_MINIMUM_QUANTITY = 1;

  const MIN_MEASUREMENT_CM = 3;
  const MAX_MEASUREMENT_CM = 80;

  const FREE_SHIPPING_TEXT =
    "GRATIS";

  const PAID_SHIPPING_TEXT =
    "Con costo";

  const RATE_LIMIT_MESSAGE =
    "Has realizado demasiados intentos. Espera un momento y vuelve a intentarlo.";

  const PRESET_OPTIONS = [
    {
      value: "5x5",
      label: "5 × 5 cm",
      widthCm: 5,
      heightCm: 5
    },
    {
      value: "7x7",
      label: "7 × 7 cm",
      widthCm: 7,
      heightCm: 7
    },
    {
      value: "10x10",
      label: "10 × 10 cm",
      widthCm: 10,
      heightCm: 10
    },
    {
      value: "12x12",
      label: "12 × 12 cm",
      widthCm: 12,
      heightCm: 12
    },
    {
      value: "custom",
      label: "Personalizado",
      widthCm: null,
      heightCm: null
    }
  ];


  /* =========================
     FORMATEADORES
  ========================== */

  const currencyFormatter =
    new Intl.NumberFormat(
      "es-MX",
      {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    );

  const quantityFormatter =
    new Intl.NumberFormat(
      "es-MX",
      {
        maximumFractionDigits: 0
      }
    );

  const formatCurrency = (
    value
  ) => {
    const amount =
      Number(value);

    return Number.isFinite(amount)
      ? currencyFormatter.format(amount)
      : "—";
  };

  const formatQuantity = (
    value
  ) => {
    const quantity =
      Number(value);

    return Number.isFinite(quantity)
      ? quantityFormatter.format(quantity)
      : "—";
  };


  /* =========================
     UTILIDADES
  ========================== */

  const debounce = (
    callback,
    delay = 450
  ) => {
    let timeoutId;

    return (...args) => {
      window.clearTimeout(
        timeoutId
      );

      timeoutId =
        window.setTimeout(
          () => callback(...args),
          delay
        );
    };
  };

  const readStoredMaterial =
    () => {
      try {
        const storedValue =
          sessionStorage.getItem(
            MATERIAL_STORAGE_KEY
          );

        if (
          !storedValue
        ) {
          return null;
        }

        const material =
          JSON.parse(
            storedValue
          );

        if (
          !material ||
          !material.id
        ) {
          return null;
        }

        return {
          id:
            String(
              material.id
            ),

          name:
            String(
              material.name ||
              ""
            ),

          slug:
            String(
              material.slug ||
              ""
            ),

          productCode:
            String(
              material.productCode ||
              ""
            ),

          imageUrl:
            String(
              material.imageUrl ||
              ""
            )
        };

      } catch (error) {
        console.error(
          "No fue posible recuperar el material seleccionado:",
          error
        );

        return null;
      }
    };

  const requestJson =
    async (
      url,
      options = {},
      signal = undefined
    ) => {
      const response =
        await fetch(
          url,
          {
            ...options,

            signal,

            headers: {
              Accept:
                "application/json",

              ...(options.body
                ? {
                    "Content-Type":
                      "application/json"
                  }
                : {}),

              ...(options.headers || {})
            }
          }
        );

      let payload;

      try {
        payload =
          await response.json();

      } catch {
        if (
          response.ok
        ) {
          throw new Error(
            "La API devolvió una respuesta que no es válida."
          );
        }

        payload = {};
      }

      if (
        !response.ok
      ) {
        const error = new Error(
          payload?.message ||
          payload?.error?.message ||
          (typeof payload?.error === "string"
            ? payload.error
            : "") ||
          `Error ${response.status}`
        );

        error.status =
          response.status;

        error.code =
          payload?.code ||
          payload?.error?.code ||
          (typeof payload?.error === "string"
            ? payload.error
            : "");

        throw error;
      }

      return payload;
    };


  /* =========================
     INICIALIZAR BUILDER
  ========================== */

  const initializeBuilder = (
    section
  ) => {
    if (
      !section ||
      section.dataset
        .builderInitialized ===
        "true"
    ) {
      return;
    }

    if (
      !window.PeguuOrder
    ) {
      console.error(
        "PeguuOrder no está disponible. Verifica que peguu-order-store.js se cargue antes del builder."
      );

      return;
    }

    section.dataset
      .builderInitialized =
      "true";


    /* =========================
       CONFIGURACIÓN
    ========================== */

    const materialsApiUrl =
      window.PeguuApi.resolve(
        section.dataset
          .materialsApiUrl
      );

    const quoteApiUrl =
      window.PeguuApi.resolve(
        section.dataset
          .quoteApiUrl
      );

    const materialsUrl =
      section.dataset
        .materialsUrl ||
      "/pages/materiales";


    const qrConfigUrl =
    section.dataset
      .qrConfigUrl ||
    "/pages/configura-qr";

    /* =========================
       SELECTORES
    ========================== */

    const $ = (
      selector
    ) =>
      section.querySelector(
        selector
      );

    const statusElement =
      $(
        "[data-builder-status]"
      );

    const contentElement =
      $(
        "[data-builder-content]"
      );

    const bannerMaterialName =
      $(
        "[data-selected-material-name]"
      );

    const sizeSelector =
      $(
        "[data-size-selector]"
      );

    const sizeTrigger =
      $(
        "[data-size-trigger]"
      );

    const sizeTriggerLabel =
      $(
        "[data-size-trigger-label]"
      );

    const sizePanel =
      $(
        "[data-size-panel]"
      );

    const sizePreset =
      $(
        "[data-size-preset]"
      );

    const sizeOptionsContainer =
      $(
        "[data-size-options]"
      );

    const customMeasurements =
      $(
        "[data-custom-measurements]"
      );

    const widthInput =
      $(
        "[data-width-input]"
      );

    const heightInput =
      $(
        "[data-height-input]"
      );

    const measurementError =
      $(
        "[data-measurement-error]"
      );

    const quantitiesSection =
      $(
        ".peguu-sticker-builder__quantities"
      );

    const quoteStatus =
      $(
        "[data-quote-status]"
      );

    const quoteTable =
      $(
        "[data-quote-table]"
      );

    const quoteList =
      $(
        "[data-quote-list]"
      );

    const customQuote =
      $(
        ".peguu-sticker-builder__custom-quote"
      );

    const customQuantityInput =
      $(
        "[data-custom-quantity]"
      );

    const customUnitPrice =
      $(
        "[data-custom-unit-price]"
      );

    const customTotal =
      $(
        "[data-custom-total]"
      );

    const customShipping =
      $(
        "[data-custom-shipping]"
      );

    const continueButton =
      $(
        "[data-continue-button]"
      );

    const flowActions =
      $(
        ".peguu-sticker-builder__flow-actions"
      );

    const orderSummary =
      $(
        "[data-peguu-order-summary]"
      );

    const backButton =
    $(
      ".peguu-flow-actions__button--back"
    );


    /* =========================
       ESTADO INTERNO
    ========================== */

    const selectedMaterial =
      readStoredMaterial();

    const storedOrder =
      window.PeguuOrder.get();

    const isSamplePack = (material) =>
      String(material?.productCode || "")
        .trim()
        .toLowerCase() === "sample_pack";

    if (
      isSamplePack(selectedMaterial) ||
      isSamplePack(storedOrder?.material) ||
      isSamplePack(storedOrder)
    ) {
      window.location.assign("/pages/materiales");
      return;
    }

    let selectedQuote =
      null;

    let selectedMeasurements =
      null;

    let sizeOptions =
      [];

    let quoteAbortController =
      null;

    let customAbortController =
      null;

    let customValidationTimeout =
      null;

    const scrollToQuantities =
      () => {
        const isMobile =
          window.matchMedia(
            "(max-width: 767px)"
          ).matches;

        if (
          !quantitiesSection ||
          (
            !isMobile &&
            !window.matchMedia(
              "(min-width: 1101px)"
            ).matches
          )
        ) {
          return;
        }

        if (isMobile) {
          const quantitiesHeading =
            quantitiesSection.querySelector("h2");

          if (!quantitiesHeading) {
            return;
          }

          const mobileViewportHeight =
            window.visualViewport?.height ||
            window.innerHeight;

          const mobileScrollOffset =
            mobileViewportHeight * 0.257;

          const targetPosition =
            quantitiesHeading
              .getBoundingClientRect()
              .top +
            window.scrollY -
            mobileScrollOffset;

          window.scrollTo({
            top: Math.max(0, targetPosition),
            behavior: "smooth"
          });

          return;
        }

        quantitiesSection.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      };

  /* =========================
        RUTA DEL BOTÓN REGRESAR
      ========================= */

      const selectedProductCode =
        String(
          selectedMaterial
            ?.productCode ||
          ""
        )
          .trim()
          .toLowerCase();


      const isQrFlow =
        selectedProductCode ===
        "qr_sticker";


      if (
        backButton
      ) {

        backButton.href =
          isQrFlow
            ? qrConfigUrl
            : materialsUrl;
      }   

    /* =========================
       PEDIDO
    ========================== */

    const buildMaterialData =
      () => ({
        id:
          selectedMaterial.id,

        name:
          selectedMaterial.name,

        slug:
          selectedMaterial.slug,

        productCode:
          selectedMaterial.productCode,

        imageUrl:
          selectedMaterial.imageUrl
      });

    const saveBaseOrder =
      () => {
        if (
          !selectedMaterial
        ) {
          return false;
        }

        const currentOrder =
          window.PeguuOrder.get() ||
          {};

        const order = {
          ...currentOrder,

          material:
            buildMaterialData()
        };

        delete order.measurements;
        delete order.quote;

        return window.PeguuOrder.set(
          order
        );
      };

    const saveOrder =
      () => {
        if (
          !selectedMaterial ||
          !selectedQuote
        ) {
          return false;
        }

        return window.PeguuOrder.update({
          material:
            buildMaterialData(),

          measurements: {
            widthCm:
              selectedQuote.widthCm,

            heightCm:
              selectedQuote.heightCm
          },

          quote: {
            quantity:
              Number(
                selectedQuote.quantity
              ),

            unitPrice:
              Number(
                selectedQuote.unitPrice
              ),

            subtotal:
              Number(
                selectedQuote.total
              ),

            shippingCost:
              selectedQuote
                .shippingCost ??
              null,

            freeShipping:
              typeof selectedQuote
                .freeShipping ===
                "boolean"
                ? selectedQuote
                    .freeShipping
                : null,

            shippingPending:
              selectedQuote
                .shippingPending,

            savingsPercent:
              Number(
                selectedQuote
                  .savingsPercent ||
                0
              ),

            finalTotal:
              Number(
                selectedQuote.finalTotal
              ),

            folio:
              selectedQuote.folio ??
              null,

            documentTitle:
              selectedQuote
                .documentTitle ??
              null,

            issuedAt:
              selectedQuote.issuedAt ??
              null,

            validUntil:
              selectedQuote.validUntil ??
              null,

            pricesIncludeTax:
              typeof selectedQuote
                .pricesIncludeTax ===
                "boolean"
                ? selectedQuote
                    .pricesIncludeTax
                : null,

            selectionType:
              selectedQuote
                .selectionType ||
              "standard"
          }
        });
      };


    /* =========================
       ESTADOS VISUALES
    ========================== */

    const showBuilderError = (
      message
    ) => {
      statusElement.hidden =
        false;

      statusElement
        .classList
        .add(
          "is-error"
        );

      statusElement.innerHTML = `
        <p>${message}</p>

        <a href="${materialsUrl}">
          Volver a materiales
        </a>
      `;

      contentElement.hidden =
        true;
    };

    const showContent =
      () => {
        statusElement.hidden =
          true;

        statusElement
          .classList
          .remove(
            "is-error"
          );

        contentElement.hidden =
          false;
      };

    const showMeasurementError = (
      message = ""
    ) => {
      measurementError.hidden =
        !message;

      measurementError.textContent =
        message;
    };

    const showQuoteStatus = (
      message,
      state = "default"
    ) => {
      quoteStatus.hidden =
        false;

      quoteStatus.textContent =
        message;

      quoteStatus
        .classList
        .toggle(
          "is-loading",
          state === "loading"
        );

      quoteStatus
        .classList
        .toggle(
          "is-error",
          state === "error"
        );
    };

    const hideQuoteStatus =
      () => {
        quoteStatus.hidden =
          true;

        quoteStatus
          .classList
          .remove(
            "is-loading",
            "is-error"
          );
      };


    /* =========================
       MATERIAL
    ========================== */

    const resolveMaterial =
      async () => {
        if (
          !selectedMaterial
        ) {
          throw new Error(
            "No encontramos el material que seleccionaste."
          );
        }

        if (
          selectedMaterial.name
        ) {
          return selectedMaterial;
        }

        if (
          !materialsApiUrl
        ) {
          throw new Error(
            "No encontramos el nombre del material."
          );
        }

        const materials =
          await window.PeguuMaterials.getMaterials({
            url: materialsApiUrl
          });

        const materialFromApi =
          materials.find(
            (material) =>
              String(
                material.id
              ) ===
              selectedMaterial.id
          );

        if (
          !materialFromApi
        ) {
          throw new Error(
            "El material seleccionado ya no está disponible."
          );
        }

        selectedMaterial.name =
          String(
            materialFromApi.name ||
            "Material Peguu"
          );

        selectedMaterial.imageUrl =
          String(
            selectedMaterial.imageUrl ||
            materialFromApi.imageUrl ||
            ""
          );

        selectedMaterial.slug =
          String(
            selectedMaterial.slug ||
            materialFromApi.slug ||
            ""
          );

        selectedMaterial.productCode =
          String(
            selectedMaterial.productCode ||
            materialFromApi.productCode ||
            ""
          );

        return selectedMaterial;
      };

      const renderMaterial = (
        material
      ) => {
        if (
          !bannerMaterialName
        ) {
          return;
        }

        const materialName =
          String(
            material?.name ||
            "Material Peguu"
          ).trim();

        bannerMaterialName.textContent =
          materialName;

        const materialBanner =
          bannerMaterialName.closest(
            ".peguu-sticker-builder__material-banner"
          );

        if (
          !materialBanner
        ) {
          return;
        }

        const isLongName =
          materialName.length > 14;

        const isVeryLongName =
          materialName.length > 22;

        materialBanner.classList.toggle(
          "has-long-name",
          isLongName
        );

        materialBanner.classList.toggle(
          "has-very-long-name",
          isVeryLongName
        );
      };

    /* =========================
       SELECTOR DE TAMAÑO
    ========================== */

    const openSizePanel =
      () => {
        if (
          !sizeSelector ||
          !sizeTrigger ||
          !sizePanel
        ) {
          return;
        }

        sizeSelector
          .classList
          .add(
            "is-open"
          );

        sizeTrigger.setAttribute(
          "aria-expanded",
          "true"
        );

        sizePanel.hidden =
          false;

        requestAnimationFrame(
          () => {
            sizePanel.scrollIntoView({
              behavior: "smooth",
              block: "nearest"
            });
          }
        );
      };

    const closeSizePanel =
      () => {
        if (
          !sizeSelector ||
          !sizeTrigger ||
          !sizePanel
        ) {
          return;
        }

        sizeSelector
          .classList
          .remove(
            "is-open"
          );

        sizeTrigger.setAttribute(
          "aria-expanded",
          "false"
        );

        sizePanel.hidden =
          true;
      };

    const toggleSizePanel =
      () => {
        if (
          !sizePanel
        ) {
          return;
        }

        if (
          sizePanel.hidden
        ) {
          openSizePanel();

        } else {
          closeSizePanel();
        }
      };

    const markSelectedSizeOption = (
      value = ""
    ) => {
      sizeOptions.forEach(
        (option) => {
          const selected =
            Boolean(value) &&
            option.dataset.value ===
              value;

          option.classList.toggle(
            "is-selected",
            selected
          );

          option.setAttribute(
            "aria-pressed",
            String(selected)
          );
        }
      );
    };

    const setPresetValue = (
      value = "",
      label = "Elige el tamaño"
    ) => {
      sizePreset.value =
        value;

      sizeTriggerLabel.textContent =
        label;

      markSelectedSizeOption(
        value
      );
    };

    const findPresetByValue = (
      value
    ) =>
      PRESET_OPTIONS.find(
        (preset) =>
          preset.value ===
          value
      );

    const findPresetByMeasurements = (
      widthCm,
      heightCm
    ) =>
      PRESET_OPTIONS.find(
        (preset) =>
          preset.value !==
            "custom" &&
          Number(
            preset.widthCm
          ) ===
            Number(widthCm) &&
          Number(
            preset.heightCm
          ) ===
            Number(heightCm)
      );


    /* =========================
       OPCIONES DE TAMAÑO
    ========================== */

    const renderSizeOptions =
      () => {
        sizeOptionsContainer.innerHTML =
          "";

        PRESET_OPTIONS.forEach(
          (preset) => {
            const option =
              document.createElement(
                "button"
              );

            option.type =
              "button";

            option.className =
              "peguu-sticker-builder__size-option";

            option.dataset.sizeOption =
              "";

            option.dataset.value =
              preset.value;

            option.textContent =
              preset.label;

            option.setAttribute(
              "aria-pressed",
              "false"
            );

            option.addEventListener(
              "click",
              () =>
                handlePresetSelection(
                  preset.value
                )
            );

            sizeOptionsContainer
              .appendChild(
                option
              );
          }
        );

        sizeOptions =
          Array.from(
            sizeOptionsContainer
              .querySelectorAll(
                "[data-size-option]"
              )
          );
      };


    /* =========================
       MEDIDAS
    ========================== */

    const setCustomMeasurementMode = (
      enabled
    ) => {
      customMeasurements.hidden =
        !enabled;

      widthInput.disabled =
        !enabled;

      heightInput.disabled =
        !enabled;

      widthInput.setAttribute(
        "aria-disabled",
        String(!enabled)
      );

      heightInput.setAttribute(
        "aria-disabled",
        String(!enabled)
      );

      if (
        enabled
      ) {
        return;
      }

      widthInput.value =
        "";

      heightInput.value =
        "";

      showMeasurementError();
    };

    const getMeasurements =
      () => {
        if (
          selectedMeasurements
        ) {
          return {
            widthCm:
              selectedMeasurements.widthCm,

            heightCm:
              selectedMeasurements.heightCm
          };
        }

        return {
          widthCm:
            Number(
              widthInput.value
            ),

          heightCm:
            Number(
              heightInput.value
            )
        };
      };

    const validateMeasurements = (
      showError = true
    ) => {
      const {
        widthCm,
        heightCm
      } =
        getMeasurements();

      const validNumbers =
        Number.isFinite(
          widthCm
        ) &&
        Number.isFinite(
          heightCm
        );

      if (
        !validNumbers
      ) {
        if (
          showError
        ) {
          showMeasurementError(
            "Ingresa un ancho y un alto válidos."
          );
        }

        return null;
      }

      const validIntegers =
        Number.isInteger(
          widthCm
        ) &&
        Number.isInteger(
          heightCm
        );

      if (
        !validIntegers
      ) {
        if (
          showError
        ) {
          showMeasurementError(
            "El ancho y el alto deben ser números enteros, sin decimales."
          );
        }

        return null;
      }

      const validRange =
        widthCm >=
          MIN_MEASUREMENT_CM &&
        widthCm <=
          MAX_MEASUREMENT_CM &&
        heightCm >=
          MIN_MEASUREMENT_CM &&
        heightCm <=
          MAX_MEASUREMENT_CM;

      if (
        !validRange
      ) {
        if (
          showError
        ) {
          showMeasurementError(
            `El ancho y el alto deben estar entre ${MIN_MEASUREMENT_CM} y ${MAX_MEASUREMENT_CM} cm.`
          );
        }

        return null;
      }

      showMeasurementError();

      return {
        widthCm,
        heightCm
      };
    };


    /* =========================
       SELECCIONES
    ========================== */

    const clearCurrentQuote = (
      updateStoredOrder = true
    ) => {
      selectedQuote =
        null;

      continueButton.disabled =
        true;

      if (
        updateStoredOrder
      ) {
        saveBaseOrder();
      }
    };

    const clearStandardSelection =
      () => {
        quoteList
          .querySelectorAll(
            ".peguu-sticker-builder__quote-row.is-selected"
          )
          .forEach(
            (row) =>
              row.classList.remove(
                "is-selected"
              )
          );
      };

    const clearCustomSelection =
      () => {
        customQuote
          ?.classList
          .remove(
            "is-selected"
          );
      };

    const clearSelections =
      () => {
        clearStandardSelection();
        clearCustomSelection();
      };

    const clearCustomQuoteValues = (
      removeSelection = true
    ) => {
      customUnitPrice.textContent =
        "—";

      customTotal.textContent =
        "—";

      customShipping.textContent =
        "—";

      customShipping
        .classList
        .add(
          "is-paid"
        );

      if (
        removeSelection
      ) {
        clearCustomSelection();
      }
    };

    const resetCustomQuote = (
      removeSelection = true
    ) => {
      customAbortController
        ?.abort();

      customAbortController =
        null;

      customQuantityInput.value =
        "";

      clearCustomQuoteValues(
        removeSelection
      );
    };

    const resetQuoteSelection = (
      updateStoredOrder = true
    ) => {
      clearSelections();

      resetCustomQuote();

      clearCurrentQuote(
        updateStoredOrder
      );
    };

    const abortQuoteRequests =
      () => {
        quoteAbortController
          ?.abort();

        customAbortController
          ?.abort();

        quoteAbortController =
          null;

        customAbortController =
          null;
      };


    /* =========================
       ESTADO DE CANTIDADES
    ========================== */

    const areQuantitiesEnabled =
      () =>
        !quantitiesSection
          ?.classList
          .contains(
            "is-disabled"
          );

    const setQuantitiesEnabled = (
      enabled,
      clearSelection = true
    ) => {
      quantitiesSection
        .classList
        .toggle(
          "is-disabled",
          !enabled
        );

      quantitiesSection
        .setAttribute(
          "aria-disabled",
          String(!enabled)
        );

      customQuantityInput.disabled =
        !enabled;

      customQuantityInput
        .setAttribute(
          "aria-disabled",
          String(!enabled)
        );

      if (
        enabled
      ) {
        return;
      }

      quoteList
        .querySelectorAll(
          ".peguu-sticker-builder__quote-row"
        )
        .forEach(
          (row) => {
            row.disabled =
              true;
          }
        );

      if (
        clearSelection
      ) {
        resetQuoteSelection();
      }
    };


    /* =========================
       FILAS DE COTIZACIÓN
    ========================== */

    const setShippingPresentation = (
      element,
      freeShipping
    ) => {
      const hasShippingStatus =
        typeof freeShipping ===
          "boolean";

      element.textContent =
        freeShipping === true
          ? FREE_SHIPPING_TEXT
          : hasShippingStatus
            ? PAID_SHIPPING_TEXT
            : "—";

      element.classList.toggle(
        "is-paid",
        freeShipping !== true
      );
    };

    const createQuoteCells = (
      quantity,
      unitPrice = null,
      total = null,
      freeShipping = null
    ) => {
      const quantityCell =
        document.createElement(
          "span"
        );

      const unitPriceCell =
        document.createElement(
          "span"
        );

      const totalCell =
        document.createElement(
          "span"
        );

      const shippingCell =
        document.createElement(
          "span"
        );

      quantityCell.textContent =
        formatQuantity(
          quantity
        );

      unitPriceCell.textContent =
        unitPrice === null
          ? "—"
          : formatCurrency(
              unitPrice
            );

      totalCell.textContent =
        total === null
          ? "—"
          : formatCurrency(
              total
            );

      shippingCell.className =
        "peguu-sticker-builder__shipping-badge";

      setShippingPresentation(
        shippingCell,
        freeShipping
      );

      return [
        quantityCell,
        unitPriceCell,
        totalCell,
        shippingCell
      ];
    };

    const createEmptyQuoteRow = (
      quantity
    ) => {
      const row =
        document.createElement(
          "button"
        );

      row.type =
        "button";

      row.className =
        "peguu-sticker-builder__quote-row";

      row.dataset.quantity =
        String(quantity);

      row.disabled =
        true;

      row.append(
        ...createQuoteCells(
          quantity
        )
      );

      return row;
    };

    const renderEmptyQuotes = (
      updateStoredOrder = true
    ) => {
      quoteList.innerHTML =
        "";

      DEFAULT_QUANTITIES.forEach(
        (quantity) => {
          quoteList.appendChild(
            createEmptyQuoteRow(
              quantity
            )
          );
        }
      );

      quoteTable.hidden =
        false;

      hideQuoteStatus();

      resetQuoteSelection(
        updateStoredOrder
      );

      setQuantitiesEnabled(
        false,
        false
      );
    };

    const renderLoadingQuotes =
      () => {
        setQuantitiesEnabled(
          false
        );

        quoteList
          .querySelectorAll(
            ".peguu-sticker-builder__quote-row"
          )
          .forEach(
            (row) => {
              const cells =
                row.querySelectorAll(
                  "span"
                );

              [1, 2, 3].forEach(
                (index) => {
                  if (
                    cells[index]
                  ) {
                    cells[index]
                      .textContent =
                      "…";
                  }
                }
              );

              if (
                cells[3]
              ) {
                cells[3].className =
                  "peguu-sticker-builder__shipping-badge is-paid";
              }

              row.disabled =
                true;

              row.classList.remove(
                "is-selected"
              );
            }
          );
      };


    /* =========================
       API DE COTIZACIÓN
    ========================== */

    const requestQuote =
      async (
        quantities,
        controller
      ) => {
        const measurements =
          validateMeasurements();

        if (
          !measurements
        ) {
          throw new Error(
            "Debes ingresar las medidas de tus stickers."
          );
        }

        showQuoteStatus(
          "Cargando precios…",
          "loading"
        );

        let payload;

        try {
          payload =
            await requestJson(
              quoteApiUrl,
              {
                method:
                  "POST",

                body:
                  JSON.stringify(
                    {
                      widthCm:
                        measurements.widthCm,

                      heightCm:
                        measurements.heightCm,

                      materialId:
                        selectedMaterial.id,

                      quantities
                    }
                  )
              },

              controller.signal
            );

        } catch (error) {
          if (
            error?.status === 429
          ) {
            error.message =
              RATE_LIMIT_MESSAGE;
          }

          throw error;
        }

        const quotes =
          payload
            ?.data
            ?.quotes;

        if (
          payload?.success !==
            true ||
          payload?.data?.valid !==
            true ||
          !Array.isArray(
            quotes
          )
        ) {
          throw new Error(
            "No fue posible obtener una cotización válida."
          );
        }

        return {
          measurements,

          quoteInfo: {
            folio:
              payload.data.folio ?? null,

            documentTitle:
              payload.data
                .documentTitle ?? null,

            issuedAt:
              payload.data.issuedAt ?? null,

            validUntil:
              payload.data.validUntil ?? null,

            pricesIncludeTax:
              typeof payload.data
                .pricesIncludeTax ===
                "boolean"
                ? payload.data
                    .pricesIncludeTax
                : null
          },

          quotes:
            quotes.filter(
              (quote) =>
                quote?.valid !==
                false
            )
        };
      };


    /* =========================
       SELECCIÓN DE COTIZACIÓN
    ========================== */

    const setSelectedQuote = (
      quote,
      measurements,
      selectionType,
      quoteInfo = {}
    ) => {
      const total =
        Number(
          quote.total
        );

      const totalWithShipping =
        Number(
          quote.totalWithShipping
        );

      const hasTotalWithShipping =
        quote.totalWithShipping !==
          null &&
        quote.totalWithShipping !==
          undefined &&
        quote.totalWithShipping !==
          "" &&
        Number.isFinite(
          totalWithShipping
        );

      const hasShippingCost =
        quote.shippingCost !== null &&
        quote.shippingCost !==
          undefined &&
        quote.shippingCost !== "" &&
        Number.isFinite(
          Number(
            quote.shippingCost
          )
        );

      const hasFreeShipping =
        typeof quote.freeShipping ===
          "boolean";

      selectedQuote = {
        ...quote,

        ...quoteInfo,

        widthCm:
          measurements.widthCm,

        heightCm:
          measurements.heightCm,

        selectionType,

        shippingPending:
          !(
            hasShippingCost &&
            hasFreeShipping
          ),

        finalTotal:
          hasTotalWithShipping
            ? totalWithShipping
            : Number.isFinite(total)
              ? total
            : 0
      };

      continueButton.disabled =
        false;

      saveOrder();
    };


    /* =========================
       CANTIDADES ESTÁNDAR
    ========================== */

    const scrollToFlowActions = () => {
      if (!flowActions) {
        return;
      }

      if (
        window.matchMedia(
          "(max-width: 767px)"
        ).matches
      ) {
        if (!orderSummary) {
          return;
        }

        const mobileViewportHeight =
          window.visualViewport?.height ||
          window.innerHeight;

        const mobileSummaryOffset =
          mobileViewportHeight * 0.383;

        const targetPosition =
          orderSummary
            .getBoundingClientRect()
            .top +
          window.scrollY -
          mobileSummaryOffset;

        window.scrollTo({
          top: Math.max(0, targetPosition),
          behavior: "smooth"
        });

        return;
      }

      const viewportMargin = 24;
      const actionsRect =
        flowActions.getBoundingClientRect();
      const visibleBottom =
        window.innerHeight - viewportMargin;

      if (
        actionsRect.top >= viewportMargin &&
        actionsRect.bottom <= visibleBottom
      ) {
        return;
      }

      const scrollDistance =
        actionsRect.bottom > visibleBottom
          ? actionsRect.bottom - visibleBottom
          : actionsRect.top - viewportMargin;

      window.scrollBy({
        top: scrollDistance,
        behavior: "smooth"
      });
    };

    const selectQuoteRow = (
      row,
      quote,
      measurements,
      quoteInfo
    ) => {
      if (
        !areQuantitiesEnabled()
      ) {
        return;
      }

      clearSelections();

      resetCustomQuote();

      row.classList.add(
        "is-selected"
      );

      setSelectedQuote(
        quote,
        measurements,
        "standard",
        quoteInfo
      );
    };

    const createQuoteRow = (
      quote,
      measurements,
      quoteInfo
    ) => {
      const row =
        document.createElement(
          "button"
        );

      row.type =
        "button";

      row.className =
        "peguu-sticker-builder__quote-row";

      row.dataset.quantity =
        String(
          quote.quantity
        );

      row.append(
        ...createQuoteCells(
          quote.quantity,
          quote.unitPrice,
          quote.total,
          quote.freeShipping
        )
      );

      row.addEventListener(
        "click",
        (event) => {
          selectQuoteRow(
            row,
            quote,
            measurements,
            quoteInfo
          );

          if (event.isTrusted) {
            scrollToFlowActions();
          }
        }
      );

      return row;
    };

    const renderQuotes = (
      quotes,
      measurements,
      quoteInfo
    ) => {
      quoteList.innerHTML =
        "";

      quotes.forEach(
        (quote) => {
          quoteList.appendChild(
            createQuoteRow(
              quote,
              measurements,
              quoteInfo
            )
          );
        }
      );

      quoteTable.hidden =
        false;

      hideQuoteStatus();

      resetQuoteSelection();

      setQuantitiesEnabled(
        true
      );
    };


    /* =========================
       CARGAR COTIZACIONES
    ========================== */

    const loadDefaultQuotes =
      async () => {
        resetQuoteSelection();

        setQuantitiesEnabled(
          false,
          false
        );

        const measurements =
          validateMeasurements();

        if (
          !measurements
        ) {
          renderEmptyQuotes();

          return false;
        }

        if (
          !quoteApiUrl
        ) {
          renderEmptyQuotes();

          showQuoteStatus(
            "No se encontró la dirección de la API de cotización.",
            "error"
          );

          return false;
        }

        quoteAbortController
          ?.abort();

        quoteAbortController =
          new AbortController();

        quoteTable.hidden =
          false;

        renderLoadingQuotes();

        try {
          const result =
            await requestQuote(
              DEFAULT_QUANTITIES,
              quoteAbortController
            );

          if (
            !result.quotes.length
          ) {
            throw new Error(
              "No existen precios disponibles para estas medidas."
            );
          }

          renderQuotes(
            result.quotes,
            result.measurements,
            result.quoteInfo
          );

          return true;

        } catch (error) {
          if (
            error.name ===
            "AbortError"
          ) {
            return false;
          }

          console.error(
            "No fue posible calcular los precios:",
            error
          );

          renderEmptyQuotes();

          showQuoteStatus(
            error.message ||
            "No fue posible calcular los precios.",
            "error"
          );

          return false;
        }
      };


    /* =========================
       SELECCIONAR MEDIDA
    ========================== */

    const handlePresetSelection =
      async (
        value
      ) => {
        abortQuoteRequests();

        resetQuoteSelection();

        setQuantitiesEnabled(
          false,
          false
        );

        const preset =
          findPresetByValue(
            value
          );

        if (
          !preset
        ) {
          return;
        }

        setPresetValue(
          preset.value,
          preset.label
        );

        closeSizePanel();

        if (
          preset.value ===
          "custom"
        ) {
          selectedMeasurements =
            null;

          setCustomMeasurementMode(
            true
          );

          renderEmptyQuotes();

          showMeasurementError();

          widthInput.focus();

          return;
        }

        selectedMeasurements = {
          widthCm:
            preset.widthCm,

          heightCm:
            preset.heightCm
        };

        setCustomMeasurementMode(
          false
        );

        showMeasurementError();

        scrollToQuantities();

        await loadDefaultQuotes();
      };


    /* =========================
       MEDIDAS PERSONALIZADAS
    ========================== */

    const handleManualMeasurement =
      async () => {
        if (
          sizePreset.value !==
          "custom"
        ) {
          return;
        }

        abortQuoteRequests();

        resetQuoteSelection();

        selectedMeasurements =
          null;

        setPresetValue(
          "custom",
          "Personalizado"
        );

        const measurements =
          validateMeasurements(
            false
          );

        if (
          !measurements
        ) {
          renderEmptyQuotes();

          const bothPresent =
            widthInput.value
              .trim() !==
              "" &&
            heightInput.value
              .trim() !==
              "";

          if (
            bothPresent
          ) {
            validateMeasurements(
              true
            );
          }

          return;
        }

        scrollToQuantities();

        await loadDefaultQuotes();
      };


    /* =========================
       CANTIDAD PERSONALIZADA
    ========================== */

    const selectCustomQuote = (
      focusInput = true
    ) => {
      if (
        !areQuantitiesEnabled()
      ) {
        return;
      }

      const alreadySelected =
        customQuote
          ?.classList
          .contains(
            "is-selected"
          );

      clearStandardSelection();

      if (
        !alreadySelected
      ) {
        clearCurrentQuote();

        clearCustomQuoteValues(
          false
        );
      }

      customQuote
        ?.classList
        .add(
          "is-selected"
        );

      if (
        focusInput
      ) {
        customQuantityInput
          ?.focus();
      }
    };

    const renderCustomQuote = (
      quote,
      measurements,
      quoteInfo
    ) => {
      hideQuoteStatus();

      customUnitPrice.textContent =
        formatCurrency(
          quote.unitPrice
        );

      customTotal.textContent =
        formatCurrency(
          quote.total
        );

      setShippingPresentation(
        customShipping,
        quote.freeShipping
      );

      clearStandardSelection();

      customQuote
        ?.classList
        .add(
          "is-selected"
        );

      setSelectedQuote(
        quote,
        measurements,
        "custom",
        quoteInfo
      );
    };

    const loadCustomQuote =
      async (
        scrollAfterSelection = false
      ) => {
        if (
          !areQuantitiesEnabled()
        ) {
          return false;
        }

        const rawValue =
          customQuantityInput
            .value
            .trim();

        const quantity =
          Number(
            rawValue
          );

        const validQuantity =
          rawValue !== "" &&
          Number.isInteger(
            quantity
          ) &&
          quantity >=
            CUSTOM_MINIMUM_QUANTITY;

        if (
          !validQuantity
        ) {
          clearCustomQuoteValues(
            false
          );

          clearCurrentQuote();

          return false;
        }

        const measurements =
          validateMeasurements();

        if (
          !measurements ||
          !quoteApiUrl
        ) {
          clearCustomQuoteValues(
            false
          );

          clearCurrentQuote();

          return false;
        }

        customAbortController
          ?.abort();

        customAbortController =
          new AbortController();

        customUnitPrice.textContent =
          "…";

        customTotal.textContent =
          "…";

        customShipping.textContent =
          "…";

        try {
          const result =
            await requestQuote(
              [quantity],
              customAbortController
            );

          const quote =
            result.quotes.find(
              (item) =>
                Number(
                  item.quantity
                ) ===
                quantity
            ) ||
            result.quotes[0];

          if (
            !quote
          ) {
            throw new Error(
              "No existe una cotización para esta cantidad."
            );
          }

          renderCustomQuote(
            quote,
            result.measurements,
            result.quoteInfo
          );

          if (scrollAfterSelection) {
            scrollToFlowActions();
          }

          return true;

        } catch (error) {
          if (
            error.name ===
            "AbortError"
          ) {
            return false;
          }

          console.error(
            "No fue posible cotizar la cantidad personalizada:",
            error
          );

          clearCustomQuoteValues(
            false
          );

          clearCurrentQuote();

          customQuote
            ?.classList
            .add(
              "is-selected"
            );

          showQuoteStatus(
            error.message ||
              "No fue posible calcular el precio.",
            "error"
          );

          return false;
        }
      };


    /* =========================
       RESTAURAR PASO 2
    ========================== */

    const restoreStoredOrder =
      async () => {
        if (
          !storedOrder ||
          !selectedMaterial
        ) {
          return false;
        }

        if (
          String(
            storedOrder
              ?.material
              ?.id
          ) !==
          String(
            selectedMaterial.id
          )
        ) {
          return false;
        }

        const widthCm =
          Number(
            storedOrder
              ?.measurements
              ?.widthCm
          );

        const heightCm =
          Number(
            storedOrder
              ?.measurements
              ?.heightCm
          );

        const quantity =
          Number(
            storedOrder
              ?.quote
              ?.quantity
          );

        const selectionType =
          storedOrder
            ?.quote
            ?.selectionType ||
          "standard";

        const validData =
          Number.isFinite(
            widthCm
          ) &&
          Number.isFinite(
            heightCm
          ) &&
          Number.isInteger(
            quantity
          ) &&
          quantity >=
            CUSTOM_MINIMUM_QUANTITY;

        if (
          !validData
        ) {
          return false;
        }

        const preset =
          findPresetByMeasurements(
            widthCm,
            heightCm
          );

        if (
          preset
        ) {
          selectedMeasurements = {
            widthCm,
            heightCm
          };

          setPresetValue(
            preset.value,
            preset.label
          );

          setCustomMeasurementMode(
            false
          );

        } else {
          selectedMeasurements =
            null;

          setPresetValue(
            "custom",
            "Personalizado"
          );

          setCustomMeasurementMode(
            true
          );

          widthInput.value =
            String(widthCm);

          heightInput.value =
            String(heightCm);
        }

        closeSizePanel();

        const loaded =
          await loadDefaultQuotes();

        if (
          !loaded
        ) {
          return false;
        }

        if (
          selectionType ===
          "custom"
        ) {
          selectCustomQuote(
            false
          );

          customQuantityInput.value =
            String(quantity);

          return await loadCustomQuote();
        }

        const standardRow =
          Array.from(
            quoteList
              .querySelectorAll(
                ".peguu-sticker-builder__quote-row"
              )
          )
          .find(
            (row) =>
              Number(
                row.dataset.quantity
              ) ===
              quantity
          );

        if (
          standardRow
        ) {
          standardRow.click();

          return true;
        }

        selectCustomQuote(
          false
        );

        customQuantityInput.value =
          String(quantity);

        return await loadCustomQuote();
      };


    /* =========================
       DEBOUNCE
    ========================== */

    const debouncedMeasurementChange =
      debounce(
        handleManualMeasurement,
        500
      );

    const debouncedCustomQuantity =
      debounce(
        () => loadCustomQuote(true),
        500
      );


    /* =========================
       EVENTOS — SELECTOR
    ========================== */

    sizeTrigger
      ?.addEventListener(
        "click",
        (event) => {
          event.stopPropagation();

          toggleSizePanel();
        }
      );

    document.addEventListener(
      "click",
      (event) => {
        if (
          !sizeSelector
            ?.contains(
              event.target
            )
        ) {
          closeSizePanel();
        }
      }
    );

    document.addEventListener(
      "keydown",
      (event) => {
        if (
          event.key !==
            "Escape" ||
          !sizePanel ||
          sizePanel.hidden
        ) {
          return;
        }

        closeSizePanel();

        sizeTrigger
          ?.focus();
      }
    );


    /* =========================
       EVENTOS — MEDIDAS
    ========================== */

    widthInput.addEventListener(
      "input",
      debouncedMeasurementChange
    );

    heightInput.addEventListener(
      "input",
      debouncedMeasurementChange
    );


    /* =========================
       EVENTOS — PERSONALIZADO
    ========================== */

    customQuote
      ?.addEventListener(
        "click",
        (event) => {
          if (
            !areQuantitiesEnabled()
          ) {
            return;
          }

          selectCustomQuote(
            event.target !==
              customQuantityInput
          );
        }
      );

    customQuantityInput.addEventListener(
      "focus",
      () => {
        if (
          areQuantitiesEnabled()
        ) {
          selectCustomQuote(
            false
          );
        }
      }
    );

    customQuantityInput.addEventListener(
      "input",
      () => {
        if (
          !areQuantitiesEnabled()
        ) {
          return;
        }

        window.clearTimeout(
          customValidationTimeout
        );

        customAbortController
          ?.abort();

        clearStandardSelection();

        customQuote
          ?.classList
          .add(
            "is-selected"
          );

        clearCurrentQuote();

        const rawValue =
          customQuantityInput
            .value
            .trim();

        if (
          rawValue ===
          ""
        ) {
          clearCustomQuoteValues(
            false
          );

          return;
        }

        const quantity =
          Number(
            rawValue
          );

        const validQuantity =
          Number.isInteger(
            quantity
          ) &&
          quantity >=
            CUSTOM_MINIMUM_QUANTITY;

        if (
          !validQuantity
        ) {
          clearCustomQuoteValues(
            false
          );

          customValidationTimeout =
            window.setTimeout(
              () => {
                const current =
                  Number(
                    customQuantityInput.value
                  );

                const stillInvalid =
                  customQuantityInput
                    .value
                    .trim() !==
                    "" &&
                  (
                    !Number.isInteger(
                      current
                    ) ||
                    current <
                      CUSTOM_MINIMUM_QUANTITY
                  );

                if (
                  !stillInvalid
                ) {
                  return;
                }

                customQuantityInput.value =
                  "";

                clearCustomQuoteValues(
                  false
                );

                clearCurrentQuote();
              },
              600
            );

          return;
        }

        debouncedCustomQuantity();
      }
    );

    customQuantityInput.addEventListener(
      "blur",
      () => {
        if (
          !areQuantitiesEnabled()
        ) {
          return;
        }

        window.clearTimeout(
          customValidationTimeout
        );

        const rawValue =
          customQuantityInput
            .value
            .trim();

        if (
          rawValue ===
          ""
        ) {
          clearCustomQuoteValues(
            false
          );

          clearCurrentQuote();

          return;
        }

        const quantity =
          Number(
            rawValue
          );

        const valid =
          Number.isInteger(
            quantity
          ) &&
          quantity >=
            CUSTOM_MINIMUM_QUANTITY;

        if (
          valid
        ) {
          return;
        }

        customAbortController
          ?.abort();

        customQuantityInput.value =
          "";

        clearCustomQuoteValues(
          false
        );

        clearCurrentQuote();
      }
    );


    /* =========================
       SIGUIENTE
    ========================== */

    continueButton.addEventListener(
      "click",
      () => {
        if (
          !selectedQuote
        ) {
          return;
        }

        if (
          !saveOrder()
        ) {
          return;
        }

        window.location.href =
          "/pages/arma-tus-stickers-diseno";
      }
    );


    /* =========================
       ARRANQUE
    ========================== */

    const start =
      async () => {
        try {
          const material =
            await resolveMaterial();

          renderMaterial(
            material
          );

          showContent();

          renderSizeOptions();

          selectedMeasurements =
            null;

          setPresetValue(
            "",
            "Elige el tamaño"
          );

          setCustomMeasurementMode(
            false
          );

          closeSizePanel();

          renderEmptyQuotes(
            false
          );

          const restored =
            await restoreStoredOrder();

          if (
            !restored
          ) {
            saveBaseOrder();
          }

        } catch (error) {
          console.error(
            "No fue posible iniciar Arma tus stickers:",
            error
          );

          showBuilderError(
            error.message ||
            "No encontramos el material seleccionado."
          );
        }
      };

    start();
  };


  /* =========================
     INICIALIZAR TODOS
  ========================== */

  const initializeAllBuilders =
    () => {
      document
        .querySelectorAll(
          "[data-peguu-sticker-builder]"
        )
        .forEach(
          initializeBuilder
        );
    };


  /* =========================
     DOM READY
  ========================== */

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initializeAllBuilders
    );

  } else {
    initializeAllBuilders();
  }


  /* =========================
     SHOPIFY THEME EDITOR
  ========================== */

  document.addEventListener(
    "shopify:section:load",
    initializeAllBuilders
  );

})();
